// Optional seed: creates a generic sample project with workstreams + tasks so a
// fresh install has something to click around in. Invoked from ProjectsList
// only when an admin clicks "Create sample project".

import { collection, doc, writeBatch } from 'firebase/firestore';
import { firestore } from '../firebase';
import { DB_PROJECTS, DB_TASKS, DB_WORKSTREAMS } from './constants';

interface SampleTask {
  label: string;
  workstreamIdx: number;
}

const SAMPLE_WORKSTREAMS: string[] = [
  'Discovery',
  'Analysis',
  'Recommendations',
  'Implementation',
  'Wrap-up',
];

const SAMPLE_TASKS: SampleTask[] = [
  // Discovery
  { workstreamIdx: 0, label: 'Kick-off meeting with stakeholders' },
  { workstreamIdx: 0, label: 'Document current state and pain points' },
  { workstreamIdx: 0, label: 'Inventory existing tools and processes' },

  // Analysis
  { workstreamIdx: 1, label: 'Identify root causes' },
  { workstreamIdx: 1, label: 'Benchmark against best practices' },
  { workstreamIdx: 1, label: 'Quantify impact and opportunity' },

  // Recommendations
  { workstreamIdx: 2, label: 'Draft proposed changes' },
  { workstreamIdx: 2, label: 'Review trade-offs with stakeholders' },
  { workstreamIdx: 2, label: 'Prioritize roadmap' },

  // Implementation
  { workstreamIdx: 3, label: 'Execute high-priority changes' },
  { workstreamIdx: 3, label: 'Track progress against milestones' },
  { workstreamIdx: 3, label: 'Adjust plan based on feedback' },

  // Wrap-up
  { workstreamIdx: 4, label: 'Produce final report' },
  { workstreamIdx: 4, label: 'Conduct handoff session' },
  { workstreamIdx: 4, label: 'Define ongoing success metrics' },
];

export const SAMPLE_PROJECT_TITLE = 'Sample engagement';
export const SAMPLE_PROJECT_DESCRIPTION = 'Generic five-phase template to explore the workspace';

export async function createSampleProject(
  title: string,
  description: string,
  createdBy: string,
): Promise<string> {
  const now = Date.now();
  const batch = writeBatch(firestore);

  const projectRef = doc(collection(firestore, DB_PROJECTS));
  const projectId = projectRef.id;
  batch.set(projectRef, {
    id: projectId,
    title,
    description,
    createdAt: now,
    createdBy,
    sortOrder: now,
  });

  const workstreamIds: string[] = SAMPLE_WORKSTREAMS.map((wsTitle, idx) => {
    const ref = doc(collection(firestore, DB_WORKSTREAMS));
    batch.set(ref, {
      id: ref.id,
      projectId,
      title: wsTitle,
      sortOrder: idx,
    });
    return ref.id;
  });

  SAMPLE_TASKS.forEach((t, idx) => {
    const ref = doc(collection(firestore, DB_TASKS));
    batch.set(ref, {
      id: ref.id,
      projectId,
      workstreamId: workstreamIds[t.workstreamIdx],
      label: t.label,
      ownerId: '',
      status: 'todo',
      updatedAt: now,
      updatedBy: createdBy,
      sortOrder: idx,
    });
  });

  // Total writes: 1 project + 5 workstreams + 15 tasks = 21, well under
  // Firestore's 500-op batch limit.
  await batch.commit();
  return projectId;
}
